// components/portal/tables/units/UnitsTable.tsx
'use client';

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { EntityView } from '@/components/entities/EntityView';
import { createScopedOrgMemberToolbarExtras } from '@/components/entities/tables/ScopedOrgMemberToolbarExtras';
import { useScopedOrgMemberFilters } from '@/components/entities/tables/useScopedOrgMemberFilters';
import { EntityBulkAction, EntityRowAction, LeasingUnitDTO, LeasingUnitFilters, UnitSortField } from '#types/types';
import { Table, TableColumnDef, TableRowSelectionContext } from '../../../portal/Table';
import { Activity, Coins, Grid2x2, Landmark, Layers, Ruler, Store, Tag } from '@/components/ui/lucide-memo';

const translations = {
  en: {
    sortIdentifier: 'Identifier',
    sortProperty: 'Property',
    sortFloor: 'Floor',
    sortSurfaceArea: 'Surface Area',
    sortStatus: 'Status',
    sortType: 'Type',
    sortRent: 'Rent',
    unitAssetHeader: 'Unit / Asset',
    occupantHeader: 'Occupant',
    surfaceHeader: 'Surface',
    statusHeader: 'Status',
    type: 'Type',
  },
  fr: {
    sortIdentifier: 'Identifiant',
    sortProperty: 'Bien',
    sortFloor: 'Etage',
    sortSurfaceArea: 'Surface',
    sortStatus: 'Statut',
    sortType: 'Type',
    sortRent: 'Loyer',
    unitAssetHeader: 'Unite / Actif',
    occupantHeader: 'Occupant',
    surfaceHeader: 'Surface',
    statusHeader: 'Statut',
    type: 'Type',
  },
};

interface UnitsTableProps {
  lockedFilters?: Partial<LeasingUnitFilters>;
  scopedIds?: string[] | null;
  assetLedgerTitle?: string;
  assetLedgerBreadcrumb?: string;
  showDefaultToolbarSelectors?: boolean;
  renderCustomToolbarHeader?: () => React.ReactNode;
  onCreateClick?: () => void;
  createButtonLabel?: string;
  rowActions?: EntityRowAction<LeasingUnitDTO>[];
  bulkActions?: EntityBulkAction<LeasingUnitDTO>[];
  enableSelection?: boolean;
  controlledSelectionIds?: string[];
  onControlledSelectionChange?: (ids: string[]) => void;
  glass?: boolean;
}

export const UnitsTable = React.memo(({
  lockedFilters,
  scopedIds,
  assetLedgerTitle = 'All units',
  assetLedgerBreadcrumb = 'Inventory / Units',
  showDefaultToolbarSelectors = true,
  renderCustomToolbarHeader,
  onCreateClick,
  createButtonLabel,
  rowActions,
  bulkActions,
  enableSelection = false,
  controlledSelectionIds,
  onControlledSelectionChange,
  glass,
}: UnitsTableProps) => {
  const { kernel } = useKernel();
  const userContext = useKernelStore((state) => state.userContext);
  const orgRegistry = useKernelStore((state) => state.inventory.Organisation.registry);
  const lang = useKernelStore((state) => state.language);
  const t = translations[lang];

  const unitLastActivity = useKernelStore(state => state.inventory.Unit.lastActivity);
  const dealLastActivity = useKernelStore(state => state.inventory.Deal.lastActivity);
  
  const [visibleUnitIds, setVisibleUnitIds] = useState<string[]>([]);
  const handleVisibleIdsChange = useCallback((ids: string[]) => setVisibleUnitIds(ids), []);

  // 2. Effet A : Hydrater Propriétés et Deals pour les unités visibles
  useEffect(() => {
    const k = kernel.current;
    if (!k || visibleUnitIds.length === 0) return;

    // On récupère les registres "frais" au moment de l'exécution
    const inv = k.getInventory();
    const unitReg = inv.Unit.registry;
    const propReg = inv.Property.registry;
    const dealReg = inv.Deal.registry;

    const propertyIdsToFetch = new Set<string>();
    const dealIdsToFetch = new Set<string>();

    visibleUnitIds.forEach(id => {
      const unit = unitReg[id];
      if (!unit) return;
      if (unit.propertyId && !propReg[unit.propertyId]) propertyIdsToFetch.add(unit.propertyId);
      if (unit.activeDealId && !dealReg[unit.activeDealId]) dealIdsToFetch.add(unit.activeDealId);
    });

    if (propertyIdsToFetch.size > 0) k.ensureInventoryIds('Property', Array.from(propertyIdsToFetch));
    if (dealIdsToFetch.size > 0) k.ensureInventoryIds('Deal', Array.from(dealIdsToFetch));

  }, [visibleUnitIds, unitLastActivity, kernel]); // Déclenché par le scroll ou l'update des units

  // 3. Effet B : Hydrater les Marques pour les Deals visibles
  useEffect(() => {
    const k = kernel.current;
    if (!k || visibleUnitIds.length === 0) return;

    const inv = k.getInventory();
    const unitReg = inv.Unit.registry;
    const dealReg = inv.Deal.registry;
    const brandReg = inv.Brand.registry;

    const brandIdsToFetch = new Set<string>();

    visibleUnitIds.forEach(id => {
      const unit = unitReg[id];
      const deal = unit?.activeDealId ? dealReg[unit.activeDealId] : null;
      if (deal?.brandId && !brandReg[deal.brandId]) {
        brandIdsToFetch.add(deal.brandId);
      }
    });

    if (brandIdsToFetch.size > 0) k.ensureInventoryIds('Brand', Array.from(brandIdsToFetch));

  }, [visibleUnitIds, dealLastActivity, kernel]);

  const currentOrg = userContext.organisationId ? orgRegistry[userContext.organisationId] : null;
  const shouldSkipDefaultOrganisationFilter =
    userContext.isFaterAdmin ||
    userContext.isFaterOrgMember ||
    currentOrg?.name?.trim().toLowerCase() === 'fater';
  const [tableFilters, setTableFilters] = useState<Partial<LeasingUnitFilters>>(() => ({
    ...(lockedFilters || {}),
    organisationId: lockedFilters?.organisationId ?? (shouldSkipDefaultOrganisationFilter ? undefined : currentOrg?.id ?? undefined),
  }));
  const {
    selectedOrgId,
    activeOrgId,
    selectedUser,
    hasDefaultToolbarSelectors,
    showOrganisationSelector,
    showUserSelector,
    handleOrgSelect,
    handleUserSelect,
  } = useScopedOrgMemberFilters<LeasingUnitFilters>({
    tableFilters,
    setTableFilters,
    lockedFilters,
    userFilterKey: 'managerId',
    showDefaultToolbarSelectors,
  });

  const sortOptions = useMemo<{ label: string; field: UnitSortField }[]>(() => [
    { label: t.sortIdentifier, field: 'IDENTIFIER' },
    { label: t.sortProperty, field: 'PROPERTY' },
    { label: t.sortFloor, field: 'FLOOR' },
    { label: t.sortSurfaceArea, field: 'AREA' },
    { label: t.sortStatus, field: 'STATUS' },
    { label: t.sortType, field: 'TYPE' },
    { label: t.sortRent, field: 'RENT' },
  ], [t]);

  const columns = useMemo<TableColumnDef[]>(() => {
    const allColumns: (TableColumnDef & { isHidden?: boolean })[] = [
      { id: 'property', icon: Landmark, minWidth: 100, header: t.sortProperty, isHidden: !!tableFilters.propertyId },
      { id: 'floor', icon: Layers, minWidth: 90, header: t.sortFloor, isHidden: !!tableFilters.floorId },
      { id: 'unit', icon: Grid2x2, minWidth: 60, header: t.unitAssetHeader },
      { id: 'status', icon: Activity, minWidth: 80, header: t.statusHeader },
      { id: 'occupant', icon: Store, minWidth: 100, header: t.occupantHeader },
      { id: 'type', icon: Tag, minWidth: 60, header: t.type },
      { id: 'surface', icon: Ruler, minWidth: 60, header: t.surfaceHeader },
      { id: 'rent', icon: Coins, minWidth: 80, header: t.sortRent },
      { id: 'actions', minWidth: 110, header: '', align: 'right' },
    ];

    return allColumns.filter((column) => !column.isHidden);
  }, [tableFilters.floorId, tableFilters.propertyId, t]);

  const renderToolbarExtras = useMemo(() => createScopedOrgMemberToolbarExtras({
    renderCustomToolbarHeader,
    hasDefaultToolbarSelectors,
    showOrganisationSelector,
    showUserSelector,
    selectedOrgId,
    selectedUser,
    activeOrgId: activeOrgId ?? undefined,
    onOrgSelect: handleOrgSelect,
    onUserSelect: handleUserSelect,
  }), [
    renderCustomToolbarHeader, hasDefaultToolbarSelectors, showOrganisationSelector, showUserSelector,
    selectedOrgId, selectedUser, activeOrgId, handleOrgSelect, handleUserSelect,
  ]);

  const renderRow = useCallback((
    unit: LeasingUnitDTO,
    isExpanded: boolean,
    toggle: () => void,
    currentColumns?: TableColumnDef[],
    selectionContext?: TableRowSelectionContext,
  ) => (
    <div className="flex w-full flex-none flex-col">
      <EntityView
        type="Unit"
        entityId={unit.id}
        variant="row"
        onClick={toggle}
        columns={currentColumns}
        rowActions={rowActions}
        isSelectable={selectionContext?.isSelectable}
        isSelected={selectionContext?.isSelected}
        onToggleSelect={selectionContext?.onToggleSelect}
        onSelectionZoneMouseDown={selectionContext?.onSelectionZoneMouseDown}
        className={isExpanded ? 'bg-white/[0.02] border-white/10' : ''}
      />
      {isExpanded && (
        <EntityView
          type="Unit"
          entityId={unit.id}
          variant="page"
          pageHasEntityRowAbove
          className="h-auto border-b border-[#2A2A2A]"
        />
      )}
    </div>
  ), [rowActions]);

  return (
    <Table
      glass={glass}
      entityType="Unit"
      initialFilters={tableFilters}
      lockedFilters={lockedFilters}
      scopedIds={scopedIds}
      columns={columns}
      assetLedgerTitle={assetLedgerTitle}
      assetLedgerBreadcrumb={assetLedgerBreadcrumb}
      sortOptions={sortOptions}
      onCreateClick={onCreateClick}
      createButtonLabel={createButtonLabel}
      bulkActions={bulkActions}
      enableSelection={enableSelection}
      controlledSelectionIds={controlledSelectionIds}
      onControlledSelectionChange={onControlledSelectionChange}
      onVisibleItemIdsChange={handleVisibleIdsChange}
      renderToolbarExtras={renderToolbarExtras}
      renderRow={renderRow}
    />
  );
});

UnitsTable.displayName = 'UnitsTable';

export const UnitsView = UnitsTable;
